<script setup>
import Dialog from "primevue/dialog";
import { ref, computed, watch, nextTick } from 'vue';
import { useUserStore } from '@/store/user-store';
import AnButton from "../common/AnButton.vue";

// Local mutual fund list and ids, just like AddStockDialog.vue
const mfList = computed(() => {
  if (props.mode === "REDEEM") {
    return props.userHoldings.map(mf => mf.fundName);
  }

  // Base list of mutual funds
  const baseMFs = [
    "Parag Parikh Flexi Cap Fund Direct Growth",
    "Kotak Multicap Fund Direct Growth",
    "ICICI Prudential Large Cap Fund Direct Growth",
    "ICICI Prudential Large & Mid Cap Direct",
    "HDFC Mid Cap Fund Direct",
    "Invesco India Smallcap Fund Direct",
    "ICICI Prudential Dividend Yield Equity Fund Direct",
    "Motilal Oswal BSE Enhanced Value Index Fund Direct",
    "HDFC Focused Fund Direct",
    "Parag Parikh ELSS Tax Saver Fund Direct",
    "Axis Overnight Fund Direct",
    "Axis Liquid Fund Direct",
    "Bandhan Ultra Short Duration Fund Direct",
    "Axis Money Market Fund Direct",
    "Axis Short Duration Fund Direct",
    "ICICI Prudential Corporate Bond Fund Direct",
    "HDFC Banking and PSU Debt Fund Direct",
    "Aditya Birla Sun Life Floating Rate Fund Direct",
    "Parag Parikh Conservative Hybrid Fund Direct",
    "ICICI Prudential Equity & Debt Fund Direct",
    "ICICI Prudential Balanced Advantage Fund Direct",
    "Nippon India Multi Asset Allocation Fund - Direct",
    "Aditya Birla Sun Life Arbitrage Fund Direct",
    "Edelweiss Equity Savings Fund Direct",
    "Motilal Oswal NASDAQ 100 ETF",
    "Mirae Asset NYSE FANG+ ETF Fund of Fund Direct"
  ];

  // If we have prefilled data and the fund name is not in the base list, add it
  if (props.prefilledData && props.prefilledData.fundName) {
    const prefilledFundName = props.prefilledData.fundName;

    // First check exact match
    let foundInBase = baseMFs.includes(prefilledFundName);

    // If no exact match and prefilledFundName doesn't contain "Fund", try variations
    if (!foundInBase && !prefilledFundName.toLowerCase().includes('fund')) {
      // Try adding "Fund Direct"
      const variation1 = prefilledFundName + " Fund Direct";
      foundInBase = baseMFs.includes(variation1);

      // If not found, try adding "Fund - Direct"
      if (!foundInBase) {
        const variation2 = prefilledFundName + " Fund - Direct";
        foundInBase = baseMFs.includes(variation2);
      }
    }

    // Only add to list if not found in base list (even with variations)
    if (!foundInBase) {
      return [...baseMFs, prefilledFundName];
    }
  }

  return baseMFs;
});

const mfIds = computed(() => {
  if (props.mode === "REDEEM") {
    return props.userHoldings.map(mf => mf.Id || mf.mutualFundId || mf.fundId || mf.id);
  }

  // Base IDs for all base funds (matching the order in mfList)
  const baseIds = [
    1,   // Parag Parikh Flexi Cap Fund Direct Growth
    2,   // Kotak Multicap Fund Direct Growth
    3,   // ICICI Prudential Large Cap Fund Direct Growth
    4,   // ICICI Prudential Large & Mid Cap Direct
    5,   // HDFC Mid Cap Fund Direct
    6,   // Invesco India Smallcap Fund Direct
    7,   // ICICI Prudential Dividend Yield Equity Fund Direct
    8,   // Motilal Oswal BSE Enhanced Value Index Fund Direct
    9,   // HDFC Focused Fund Direct
    10,  // Parag Parikh ELSS Tax Saver Fund Direct
    11,  // Axis Overnight Fund Direct
    12,  // Axis Liquid Fund Direct
    13,  // Bandhan Ultra Short Duration Fund Direct
    14,  // Axis Money Market Fund Direct
    15,  // Axis Short Duration Fund Direct
    16,  // ICICI Prudential Corporate Bond Fund Direct
    17,  // HDFC Banking and PSU Debt Fund Direct
    18,  // Aditya Birla Sun Life Floating Rate Fund Direct
    19,  // Parag Parikh Conservative Hybrid Fund Direct
    20,  // ICICI Prudential Equity & Debt Fund Direct
    21,  // ICICI Prudential Balanced Advantage Fund Direct
    22,  // Nippon India Multi Asset Allocation Fund - Direct
    23,  // Aditya Birla Sun Life Arbitrage Fund Direct
    24,  // Edelweiss Equity Savings Fund Direct
    25,  // Motilal Oswal NASDAQ 100 ETF
    26   // Mirae Asset NYSE FANG+ ETF Fund of Fund Direct
  ];

  // If we have prefilled data, add its ID if it's not in base list
  if (props.prefilledData && props.prefilledData.fundName) {
    const prefilledFundName = props.prefilledData.fundName;
    const baseMFs = [
      "Parag Parikh Flexi Cap Fund Direct Growth",
      "Kotak Multicap Fund Direct Growth",
      "ICICI Prudential Large Cap Fund Direct Growth",
      "ICICI Prudential Large & Mid Cap Direct",
      "HDFC Mid Cap Fund Direct",
      "Invesco India Smallcap Fund Direct",
      "ICICI Prudential Dividend Yield Equity Fund Direct",
      "Motilal Oswal BSE Enhanced Value Index Fund Direct",
      "HDFC Focused Fund Direct",
      "Parag Parikh ELSS Tax Saver Fund Direct",
      "Axis Overnight Fund Direct",
      "Axis Liquid Fund Direct",
      "Bandhan Ultra Short Duration Fund Direct",
      "Axis Money Market Fund Direct",
      "Axis Short Duration Fund Direct",
      "ICICI Prudential Corporate Bond Fund Direct",
      "HDFC Banking and PSU Debt Fund Direct",
      "Aditya Birla Sun Life Floating Rate Fund Direct",
      "Parag Parikh Conservative Hybrid Fund Direct",
      "ICICI Prudential Equity & Debt Fund Direct",
      "ICICI Prudential Balanced Advantage Fund Direct",
      "Nippon India Multi Asset Allocation Fund - Direct",
      "Aditya Birla Sun Life Arbitrage Fund Direct",
      "Edelweiss Equity Savings Fund Direct",
      "Motilal Oswal NASDAQ 100 ETF",
      "Mirae Asset NYSE FANG+ ETF Fund of Fund Direct"
    ];

    // First check exact match
    let matchIndex = baseMFs.findIndex(fund => fund === prefilledFundName);

    // If no exact match and prefilledFundName doesn't contain "Fund", try variations
    if (matchIndex === -1 && !prefilledFundName.toLowerCase().includes('fund')) {
      console.log(`No exact match for "${prefilledFundName}", trying variations...`);

      // Try adding "Fund Direct"
      const variation1 = prefilledFundName + " Fund Direct";
      matchIndex = baseMFs.findIndex(fund => fund === variation1);
      console.log(`Trying "${variation1}":`, matchIndex !== -1 ? `Found at index ${matchIndex}` : 'Not found');

      // If not found, try adding "Fund - Direct"
      if (matchIndex === -1) {
        const variation2 = prefilledFundName + " Fund - Direct";
        matchIndex = baseMFs.findIndex(fund => fund === variation2);
        console.log(`Trying "${variation2}":`, matchIndex !== -1 ? `Found at index ${matchIndex}` : 'Not found');
      }
    }

    if (matchIndex === -1 && !baseMFs.includes(prefilledFundName)) {
      return [...baseIds, props.prefilledData.fundId || 999]; // Use provided ID or fallback
    } else if (matchIndex !== -1) {
      console.log(`Found matching fund at index ${matchIndex}: "${baseMFs[matchIndex]}" for "${prefilledFundName}"`);
      // Fund found in base list, so don't add extra ID - will use the base ID
    }
  }

  return baseIds;
});

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String,
    default: "BUY", // "BUY" or "REDEEM"
    validator: (value) => ["BUY", "REDEEM"].includes(value)
  },
  userHoldings: {
    type: Array,
    default: () => []
  },
  prefilledData: {
    type: Object,
    default: null
  }
});
const emit = defineEmits(["update:modelValue", "update:mode", "added"]);
const store = useUserStore();

const selectedMF = ref("");
const selectedMFId = ref(null);
const currentNAV = ref("");
const amountInvested = ref("");
const numberOfUnits = ref("");
const transactionDate = ref("");
const navLoading = ref(false);
const error = ref("");
const useCurrentPrice = ref(false); // For REDEEM mode: use current NAV or custom NAV

const filteredMFList = ref([]);
const showDropdown = ref(false);

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Check if we have prefilled data
    if (props.prefilledData) {
      console.log('Prefilling mutual fund dialog with data:', props.prefilledData)
      selectedMF.value = props.prefilledData.fundName || props.prefilledData.mutualFundName || ""
      selectedMFId.value = props.prefilledData.fundId || null
      currentNAV.value = props.prefilledData.currentNAV || props.prefilledData.nav || ""
      amountInvested.value = props.prefilledData.amountInvested || ""
      numberOfUnits.value = props.prefilledData.numberOfUnits || ""
      transactionDate.value = props.prefilledData.transactionDate || ""

      // If we have a fund name but no ID, or if we need to fetch NAV, call fetchCurrentNAV
      if (selectedMF.value && (!selectedMFId.value || !currentNAV.value)) {
        console.log('Calling fetchCurrentNAV for prefilled fund:', selectedMF.value)
        fetchCurrentNAV()
      }
    } else {
      // Reset to empty values
      selectedMF.value = ""
      selectedMFId.value = null
      currentNAV.value = ""
      amountInvested.value = ""
      numberOfUnits.value = ""
      transactionDate.value = ""
    }

    navLoading.value = false
    error.value = ""
    filteredMFList.value = []
    showDropdown.value = false
    useCurrentPrice.value = false // Reset for REDEEM mode
  }
});

const isFieldsEnabled = computed(() => {
  if (!selectedMF.value) return false;

  // For REDEEM mode, check if fund exists in user holdings
  if (props.mode === "REDEEM") {
    return props.userHoldings.some(mf => mf.fundName === selectedMF.value);
  }

  // For BUY mode, check if fund exists in our predefined list or is a valid prefilled fund
  const baseMFs = [
    "Parag Parikh Flexi Cap Fund Direct Growth",
    "Kotak Multicap Fund Direct Growth",
    "ICICI Prudential Large Cap Fund Direct Growth",
    "ICICI Prudential Large & Mid Cap Direct",
    "HDFC Mid Cap Fund Direct",
    "Invesco India Smallcap Fund Direct",
    "ICICI Prudential Dividend Yield Equity Fund Direct",
    "Motilal Oswal BSE Enhanced Value Index Fund Direct",
    "HDFC Focused Fund Direct",
    "Parag Parikh ELSS Tax Saver Fund Direct",
    "Axis Overnight Fund Direct",
    "Axis Liquid Fund Direct",
    "Bandhan Ultra Short Duration Fund Direct",
    "Axis Money Market Fund Direct",
    "Axis Short Duration Fund Direct",
    "ICICI Prudential Corporate Bond Fund Direct",
    "HDFC Banking and PSU Debt Fund Direct",
    "Aditya Birla Sun Life Floating Rate Fund Direct",
    "Parag Parikh Conservative Hybrid Fund Direct",
    "ICICI Prudential Equity & Debt Fund Direct",
    "ICICI Prudential Balanced Advantage Fund Direct",
    "Nippon India Multi Asset Allocation Fund - Direct",
    "Aditya Birla Sun Life Arbitrage Fund Direct",
    "Edelweiss Equity Savings Fund Direct",
    "Motilal Oswal NASDAQ 100 ETF",
    "Mirae Asset NYSE FANG+ ETF Fund of Fund Direct"
  ];

  // Check exact match first
  if (baseMFs.includes(selectedMF.value)) {
    return true;
  }

  // Check if it's a prefilled fund that can be matched with variations
  if (props.prefilledData && props.prefilledData.fundName === selectedMF.value) {
    // Method 1: Try appending different variations to prefilled name
    const prefilledFundName = selectedMF.value.trim();
    const appendVariations = [
      prefilledFundName + " Direct",
      prefilledFundName + " Fund Direct",
      prefilledFundName + " Fund - Direct"
    ];

    // Check append variations
    for (const variation of appendVariations) {
      if (baseMFs.includes(variation)) {
        return true;
      }
    }

    // Method 2: Try trimming base fund names from " Fund" onwards
    const foundByTrimming = baseMFs.some(baseFund => {
      let trimmedBaseFundName = baseFund.trim();

      // Find the position of " Fund" (with space to avoid partial matches)
      const fundIndex = baseFund.toLowerCase().indexOf(' fund');
      if (fundIndex !== -1) {
        trimmedBaseFundName = baseFund.substring(0, fundIndex).trim();
      }

      // Compare trimmed base fund name with prefilled name (case insensitive)
      return trimmedBaseFundName.toLowerCase() === prefilledFundName.toLowerCase();
    });

    if (foundByTrimming) {
      return true;
    }

    // Method 3: Try fuzzy matching as fallback
    const searchTerm = selectedMF.value.toLowerCase();
    const foundByFuzzy = baseMFs.some((name) => {
      const fundName = name.toLowerCase();

      // Check if predefined fund name starts with the search term
      if (fundName.startsWith(searchTerm)) return true;

      // Check if search term starts with predefined fund name (without "fund direct" etc.)
      const coreSearchTerm = searchTerm.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();
      const coreFundName = fundName.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();

      if (coreSearchTerm === coreFundName) return true;

      return false;
    });

    if (foundByFuzzy) {
      return true;
    }
  }

  // Default check against mfList for any other cases
  return mfList.value && mfList.value.includes(selectedMF.value);
});

// Helper functions for REDEEM mode

// Get max units user can redeem
const maxRedeemUnits = computed(() => {
  if (props.mode === "REDEEM" && selectedMF.value) {
    const holding = props.userHoldings.find(mf => mf.fundName === selectedMF.value);
    return holding ? holding.numberOfUnits : 0;
  }
  return null;
});

const handleInput = (event) => {
  const query = event.target.value.toLowerCase();
  if (query.length >= 1) {
    filteredMFList.value = mfList.value
      .filter((mf) => mf.toLowerCase().startsWith(query))
      .sort((a, b) => a.localeCompare(b));
    showDropdown.value = true;
  } else {
    filteredMFList.value = [];
    showDropdown.value = false;
  }
};

const handleBlur = () => {
  setTimeout(() => {
    showDropdown.value = false;
  }, 150);
};

const selectMF = (mf) => {
  selectedMF.value = mf;
  showDropdown.value = false;
  fetchCurrentNAV();
};

const fetchCurrentNAV = async () => {
  console.log("fetchCurrentNAV called for:", selectedMF.value);
  console.log("Current mode:", props.mode);
  console.log("User holdings:", props.userHoldings);
  console.log("mfList:", mfList.value);
  console.log("mfIds:", mfIds.value);

  let idx = -1;

  if (props.mode === "REDEEM") {
    // For REDEEM mode, search in the user holdings list (which is what mfList contains)
    idx = mfList.value.findIndex((name) => name === selectedMF.value);
    console.log("REDEEM mode - searching in mfList, found at index:", idx);
  } else {
    // For BUY mode, use the base list matching logic
    const baseMFs = [
      "Parag Parikh Flexi Cap Fund Direct Growth",
      "Kotak Multicap Fund Direct Growth",
      "ICICI Prudential Large Cap Fund Direct Growth",
      "ICICI Prudential Large & Mid Cap Direct",
      "HDFC Mid Cap Fund Direct",
      "Invesco India Smallcap Fund Direct",
      "ICICI Prudential Dividend Yield Equity Fund Direct",
      "Motilal Oswal BSE Enhanced Value Index Fund Direct",
      "HDFC Focused Fund Direct",
      "Parag Parikh ELSS Tax Saver Fund Direct",
      "Axis Overnight Fund Direct",
      "Axis Liquid Fund Direct",
      "Bandhan Ultra Short Duration Fund Direct",
      "Axis Money Market Fund Direct",
      "Axis Short Duration Fund Direct",
      "ICICI Prudential Corporate Bond Fund Direct",
      "HDFC Banking and PSU Debt Fund Direct",
      "Aditya Birla Sun Life Floating Rate Fund Direct",
      "Parag Parikh Conservative Hybrid Fund Direct",
      "ICICI Prudential Equity & Debt Fund Direct",
      "ICICI Prudential Balanced Advantage Fund Direct",
      "Nippon India Multi Asset Allocation Fund - Direct",
      "Aditya Birla Sun Life Arbitrage Fund Direct",
      "Edelweiss Equity Savings Fund Direct",
      "Motilal Oswal NASDAQ 100 ETF",
      "Mirae Asset NYSE FANG+ ETF Fund of Fund Direct"
    ];

    // First try exact match in base list
    idx = baseMFs.findIndex((name) => name === selectedMF.value);

    // If no exact match, try variations with "Fund Direct" or "Fund - Direct"
    if (idx === -1 && selectedMF.value) {
      console.log("No exact match found, trying variations for:", selectedMF.value);

      // If the selected MF doesn't contain "Fund", try adding "Fund Direct"
      if (!selectedMF.value.toLowerCase().includes('fund')) {
        const variation1 = selectedMF.value + " Fund Direct";
        idx = baseMFs.findIndex((name) => name === variation1);
        console.log(`Trying "${variation1}":`, idx !== -1 ? `Found at index ${idx} in base list` : 'Not found');

        // If not found, try adding "Fund - Direct"
        if (idx === -1) {
          const variation2 = selectedMF.value + " Fund - Direct";
          idx = baseMFs.findIndex((name) => name === variation2);
          console.log(`Trying "${variation2}":`, idx !== -1 ? `Found at index ${idx} in base list` : 'Not found');
        }
      }

      // If still no match, try fuzzy matching for similar names
      if (idx === -1) {
        const searchTerm = selectedMF.value.toLowerCase();
        console.log("No variation match found, trying fuzzy matching for:", searchTerm);

        idx = baseMFs.findIndex((name) => {
          const fundName = name.toLowerCase();

          // Check if predefined fund name starts with the search term
          if (fundName.startsWith(searchTerm)) return true;

          // Check if search term starts with predefined fund name (without "fund direct" etc.)
          const coreSearchTerm = searchTerm.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();
          const coreFundName = fundName.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();

          if (coreSearchTerm === coreFundName) return true;

          // For ICICI funds specifically, handle "ICICI Pru" vs "ICICI Prudential"
          if (searchTerm.includes('icici') && fundName.includes('icici')) {
            const normalizedSearch = searchTerm.replace('pru ', 'prudential ');
            const normalizedFund = fundName.replace('pru ', 'prudential ');
            if (normalizedFund.startsWith(normalizedSearch) || normalizedSearch.startsWith(normalizedFund.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim())) {
              return true;
            }
          }

          return false;
        });

        if (idx !== -1) {
          console.log(`Fuzzy match found: "${selectedMF.value}" matched with "${baseMFs[idx]}" at index ${idx} in base list`);
        }
      }
    }

    console.log("BUY mode - searching in base list, found at index:", idx);
  }

  selectedMFId.value = idx !== -1 ? mfIds.value[idx] : null;

  console.log("ID lookup - idx:", idx, "selectedMFId:", selectedMFId.value);
  console.log("mfIds.value length:", mfIds.value ? mfIds.value.length : 'null');
  console.log("mfIds.value:", mfIds.value);
  console.log("mfIds.value[idx]:", idx !== -1 && mfIds.value ? mfIds.value[idx] : 'N/A');

  // Double-check: If idx is valid but selectedMFId is still null, there might be an array issue
  if (idx !== -1 && selectedMFId.value === null) {
    console.error("Array access issue - idx is valid but mfIds.value[idx] is null/undefined");

    // Fallback: manually get the base IDs array (only for BUY mode)
    if (props.mode === "BUY") {
      const baseIds = [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26
      ];

      if (idx < baseIds.length) {
        selectedMFId.value = baseIds[idx];
        console.log("Used manual fallback ID:", selectedMFId.value, "for index:", idx);
      }
    }
  }

  // If we couldn't find an ID from the predefined list, generate a fallback
  if (selectedMFId.value === null && selectedMF.value) {
    // For model portfolio funds not in our predefined list, use a hash-based ID
    const hashId = selectedMF.value.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    selectedMFId.value = Math.abs(hashId) % 1000 + 1000; // Generate ID between 1000-1999
    console.log("Generated fallback ID:", selectedMFId.value, "for fund:", selectedMF.value);
  }

  // Skip fetching if we already have current NAV from prefilled data and this is first load
  if (props.prefilledData && props.prefilledData.currentNAV && currentNAV.value) {
    console.log("Using prefilled NAV:", currentNAV.value);
    return;
  }

  if (selectedMF.value && selectedMFId.value != null) {
    navLoading.value = true;
    try {
      console.log("Fetching NAV for:", selectedMF.value, "ID:", selectedMFId.value);
      const nav = await store.getStocksMFCurrentPrice(selectedMF.value, "M");
      currentNAV.value = nav.currentNAV || "";
      console.log("Fetched NAV:", currentNAV.value);
    } catch (error) {
      console.error("Error fetching NAV:", error);
      currentNAV.value = "";
    }
    navLoading.value = false;
  } else {
    currentNAV.value = "";
    console.warn("No valid mutual fund selected. MF:", selectedMF.value, "ID:", selectedMFId.value);
  }
};

// Handle current price checkbox change
const handleCurrentPriceChange = () => {
  console.log("Use current price checkbox changed:", useCurrentPrice.value);
  if (useCurrentPrice.value) {
    // When checkbox is checked, copy current NAV to redemption price input
    amountInvested.value = currentNAV.value;
    // Set current date when using current price
    transactionDate.value = new Date().toISOString().slice(0,10);
  } else {
    // When checkbox is unchecked, clear the amount field and date
    amountInvested.value = "";
    transactionDate.value = "";
  }
};

// Handle mode switching
const switchMode = (newMode) => {
  emit("update:mode", newMode);
  // Reset form when switching modes
  resetForm();
};

// Reset form function
const resetForm = () => {
  selectedMF.value = "";
  selectedMFId.value = null;
  currentNAV.value = "";
  amountInvested.value = "";
  numberOfUnits.value = "";
  transactionDate.value = "";
  navLoading.value = false;
  error.value = "";
  filteredMFList.value = [];
  showDropdown.value = false;
  useCurrentPrice.value = false;
};

// Calculate redemption amount for REDEEM transactions
const calculateRedemptionAmount = () => {
  if (props.mode !== "REDEEM" || !selectedMF.value || !numberOfUnits.value) {
    return 0;
  }

  // 1) Get amountInvested from dashboardData for the mutual fund being redeemed
  const mutualFunds = store.dashboardData.mutualFunds || [];
  const holding = mutualFunds.find(fund => fund.fundName === selectedMF.value);

  if (!holding) {
    return 0;
  }

  // 2) Get per unit investment = amountInvested / numberOfUnits
  const perUnitInvestment = parseFloat(holding.amountInvested) / parseFloat(holding.numberOfUnits);

  // 3) numberOfUnits will be negative (handled in payload)
  // 4) amountInvested will be numberOfUnits * perUnitInvestment, and negative
  const redeemUnits = parseFloat(numberOfUnits.value);
  const redemptionAmount = -(redeemUnits * perUnitInvestment);

  return redemptionAmount;
};

const addMutualFund = async () => {
  console.log('addMutualFund validation check:', {
    selectedMFId: selectedMFId.value,
    selectedMF: selectedMF.value,
    amountInvested: amountInvested.value,
    numberOfUnits: numberOfUnits.value,
    transactionDate: transactionDate.value,
    currentNAV: currentNAV.value
  })

  if (!selectedMFId.value || !selectedMF.value || !amountInvested.value || !numberOfUnits.value || !transactionDate.value || !currentNAV.value) {
    const missingFields = []
    if (!selectedMFId.value) missingFields.push('selectedMFId')
    if (!selectedMF.value) missingFields.push('selectedMF')
    if (!amountInvested.value) missingFields.push('amountInvested')
    if (!numberOfUnits.value) missingFields.push('numberOfUnits')
    if (!transactionDate.value) missingFields.push('transactionDate')
    if (!currentNAV.value) missingFields.push('currentNAV')

    console.log('Missing fields:', missingFields)
    error.value = `Please fill all fields. Missing: ${missingFields.join(', ')}`;
    return;
  }

  // For REDEEM mode: validate max units
  if (props.mode === "REDEEM" && numberOfUnits.value > maxRedeemUnits.value) {
    error.value = `Cannot redeem more than ${maxRedeemUnits.value} units`;
    return;
  }

  // Check if mutual fund already exists in user's portfolio
  const mutualFunds = store.dashboardData.mutualFunds || [];

  // First try exact match
  let exists = mutualFunds.some(fund => fund.fundName === selectedMF.value);
  console.log('Step 1 - Exact match check:', exists);

  // If no exact match and this is prefilled flow, try variations for prefilled funds
  if (!exists && selectedMF.value && props.prefilledData) {
    console.log('Step 2 - Prefilled flow: Trying variations...');

    const prefilledFundName = selectedMF.value.trim();
    console.log('Original prefilled fund name:', prefilledFundName);

    // Method 1: Try appending different variations to prefilled name
    const appendVariations = [
      prefilledFundName + " Direct",
      prefilledFundName + " Fund Direct",
      prefilledFundName + " Fund - Direct"
    ];

    for (const variation of appendVariations) {
      console.log(`Trying append variation: "${variation}"`);
      const found = mutualFunds.some(fund => fund.fundName === variation);
      if (found) {
        console.log(`✅ Found match with append variation: "${variation}"`);
        exists = true;
        break;
      } else {
        console.log(`❌ No match for append variation: "${variation}"`);
      }
    }

    // Method 2: If no match, try trimming portfolio fund names from "Fund" onwards
    if (!exists) {
      console.log('Step 3 - Trying portfolio fund trimming from "Fund" onwards...');

      exists = mutualFunds.some(fund => {
        // Trim portfolio fund name from "Fund" onwards and remove whitespace
        let trimmedPortfolioName = fund.fundName.trim();

        // Find the position of " Fund" (with space to avoid partial matches)
        const fundIndex = fund.fundName.toLowerCase().indexOf(' fund');
        if (fundIndex !== -1) {
          trimmedPortfolioName = fund.fundName.substring(0, fundIndex).trim();
        }

        console.log(`Checking: "${fund.fundName}" -> trimmed: "${trimmedPortfolioName}" vs prefilled: "${prefilledFundName}"`);

        // Compare trimmed portfolio name with prefilled name (case insensitive)
        if (trimmedPortfolioName.toLowerCase() === prefilledFundName.toLowerCase()) {
          console.log(`✅ Found match after trimming: "${trimmedPortfolioName}" matches "${prefilledFundName}"`);
          return true;
        }

        return false;
      });

      console.log('Result after portfolio fund trimming:', exists);
    }

    // Method 3: If still no match, try the existing normalization logic as final fallback
    if (!exists) {
      console.log('Step 4 - Final fallback: Trying normalized comparison...');

      // Normalize function for comparison
      const normalize = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
      const selectedNormalized = normalize(selectedMF.value);
      console.log('selectedNormalized:', selectedNormalized);

      exists = mutualFunds.some(fund => {
        const portfolioNormalized = normalize(fund.fundName);
        console.log(`Checking portfolio fund: "${fund.fundName}" -> normalized: "${portfolioNormalized}"`);

        // Exact normalized match
        if (selectedNormalized === portfolioNormalized) {
          console.log('✅ Found exact normalized match!');
          return true;
        }

        // Check if portfolio fund name without "Fund Direct" matches prefilled
        const portfolioCore = portfolioNormalized.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();
        console.log(`Portfolio core: "${portfolioCore}" vs selected: "${selectedNormalized}"`);
        if (selectedNormalized === portfolioCore) {
          console.log('✅ Found core match!');
          return true;
        }

        console.log('❌ No match found for this portfolio fund');
        return false;
      });
    }

    console.log('Final result after all prefilled variations:', exists);
  } else if (!exists && selectedMF.value && !props.prefilledData) {
    console.log('Step 2 - Non-prefilled flow: Using standard normalization...');

    // For non-prefilled flows, use the existing normalization logic
    const normalize = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
    const selectedNormalized = normalize(selectedMF.value);

    exists = mutualFunds.some(fund => {
      const portfolioNormalized = normalize(fund.fundName);

      // Standard normalization checks for non-prefilled flows
      if (selectedNormalized === portfolioNormalized) return true;

      const portfolioCore = portfolioNormalized.replace(/\s+(fund|direct|plan|growth)(\s|$)/g, '').trim();
      if (selectedNormalized === portfolioCore) return true;

      return false;
    });
  }

  console.log('Checking if fund exists in portfolio:');
  console.log('- selectedMF.value:', selectedMF.value);
  console.log('- mutualFunds in portfolio:', mutualFunds.map(f => f.fundName));
  console.log('- exists (final result):', exists);
  console.log('- isPrefilled:', !!props.prefilledData);  let transactionType = "A"; // Add new
  if (props.mode === "REDEEM") {
    transactionType = "R"; // Redeem
  } else {
    transactionType = exists ? "M" : "A";
  }

  console.log('Final transactionType:', transactionType);

  const payload = {
    mutualFundId: selectedMFId.value,
    mutualFundName: selectedMF.value,
    amountInvested: transactionType === "R" ? calculateRedemptionAmount() : parseFloat(amountInvested.value),
    numberOfUnits: transactionType === "R" ? -parseFloat(numberOfUnits.value) : parseFloat(numberOfUnits.value),
    transactionDate: transactionDate.value,
    transactionType,
    currentNAV: parseFloat(currentNAV.value),
  };

  try {
    await store.addMutualFund(payload);
    emit("added", payload);
    emit("update:modelValue", false);
  } catch {
    error.value = `Failed to ${props.mode.toLowerCase()} mutual fund.`;
  }
};

const closeDialog = () => {
  emit("update:modelValue", false);
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="false"
    :dismissable-mask="false"
    @update:visible="(value) => emit('update:modelValue', value)"
  >
    <template #header>
      <div class="d-flex align-items-center position-relative w-100 p-20 dialog-header">
        <!-- Mode Toggle Buttons - Left Top Corner -->
        <div class="mode-toggle-container-left">
          <AnButton
            label="Buy"
            :variant="props.mode === 'BUY' ? 'primary' : 'tab'"
            @click="switchMode('BUY')"
            class="mode-toggle-btn-small"
          />
          <AnButton
            label="Redeem"
            :variant="props.mode === 'REDEEM' ? 'primary' : 'tab'"
            @click="switchMode('REDEEM')"
            class="mode-toggle-btn-small"
          />
        </div>

        <span class="text-center text-primary w-100 font-20 font-weight-700">
          {{ props.mode === 'REDEEM' ? 'Redeem Mutual Fund' : 'Add or Update-Existing Mutual Fund Holding' }}
        </span>
        <i
          @click="closeDialog"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>
    <div class="ph-30 pb-30">
      <!-- Step 1: Mutual Fund Selection -->
      <div class="step-section">
        <div class="step-header">
          <div class="step-indicator">
            <span class="step-number">1</span>
          </div>
          <span class="step-title">Mutual Fund Selection</span>
        </div>
        <div class="d-grid stock-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="mfName" class="font-weight-600">Mutual Fund Name</label>
            <div class="custom-autocomplete">
              <input
                id="mfName"
                v-model="selectedMF"
                @input="handleInput"
                @focus="showDropdown = true"
                @blur="handleBlur"
                placeholder="Enter mutual fund name"
                class="input-field autocomplete-input"
                autocomplete="off"
              />
              <div
                v-if="showDropdown && filteredMFList.length > 0"
                class="custom-dropdown"
              >
                <div
                  v-for="mf in filteredMFList"
                  :key="mf"
                  class="dropdown-item"
                  @mousedown="selectMF(mf)"
                >
                  {{ mf }}
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex flex-direction-column">
            <label for="currentNAV" class="font-weight-600">Current NAV</label>
            <input
              id="currentNAV"
              v-model="currentNAV"
              type="text"
              class="input-field current-price-field"
              placeholder="Current NAV will be fetched"
              readonly
            />
          </div>
        </div>
      </div>
      <!-- Step 2: Transaction Details -->
      <div class="step-section" :class="{ 'disabled-section': !isFieldsEnabled }">
        <div class="step-header">
          <div class="step-indicator" :class="{ 'active': isFieldsEnabled }">
            <span class="step-number">2</span>
          </div>
          <span class="step-title">Transaction Details</span>
        </div>
        <div class="d-grid transaction-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="amountInvested" class="font-weight-600 d-flex align-items-center gap-10">
              {{ props.mode === 'REDEEM' ? 'Redemption NAV' : 'Amount Invested' }}
              <div v-if="props.mode === 'REDEEM'" class="d-flex align-items-center gap-5 ml-10">
                <input
                  type="checkbox"
                  id="useCurrentPrice"
                  v-model="useCurrentPrice"
                  @change="handleCurrentPriceChange"
                />
                <label for="useCurrentPrice" class="text-sm font-weight-400">Use current NAV (₹{{ currentNAV }})</label>
              </div>
            </label>
            <input
              id="amountInvested"
              v-model="amountInvested"
              type="text"
              :disabled="!isFieldsEnabled || (props.mode === 'REDEEM' && useCurrentPrice)"
              :placeholder="props.mode === 'REDEEM' ? (useCurrentPrice ? `₹${currentNAV}` : 'Enter redemption NAV') : 'Enter amount invested'"
              class="input-field"
              :class="{ 'disabled-field': props.mode === 'REDEEM' && useCurrentPrice }"
              @input="amountInvested = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
          </div>
          <div class="d-flex flex-direction-column">
            <label for="numberOfUnits" class="font-weight-600">
              {{ props.mode === 'REDEEM' ? 'Units to Redeem' : 'Units Allocated' }}
              <span v-if="props.mode === 'REDEEM' && maxRedeemUnits !== null" class="text-muted">
                (Max: {{ maxRedeemUnits }})
              </span>
            </label>
            <input
              id="numberOfUnits"
              v-model="numberOfUnits"
              type="text"
              :disabled="!isFieldsEnabled"
              :placeholder="props.mode === 'REDEEM' ? `Enter units to redeem (max ${maxRedeemUnits || 0})` : 'No. of Units Allocated'"
              class="input-field"
              :class="{ 'border-red-500': props.mode === 'REDEEM' && numberOfUnits > maxRedeemUnits }"
              @input="numberOfUnits = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
            <small v-if="props.mode === 'REDEEM' && numberOfUnits > maxRedeemUnits" class="error-text">
              Cannot redeem more than {{ maxRedeemUnits }} units
            </small>
          </div>
          <div class="d-flex flex-direction-column">
            <label for="transactionDate" class="font-weight-600">Date</label>
            <input
              id="transactionDate"
              v-model="transactionDate"
              type="date"
              :disabled="!isFieldsEnabled || (props.mode === 'REDEEM' && useCurrentPrice)"
              placeholder="Select date"
              class="input-field"
              :class="{ 'disabled-field': props.mode === 'REDEEM' && useCurrentPrice }"
              :max="new Date().toISOString().slice(0,10)"
            />
          </div>
        </div>
      </div>
      <div v-if="error" class="error-msg">{{ error }}</div>
    </div>
    <template #footer>
      <div class="d-flex justify-center gap-20 w-100 p-10">
        <AnButton
          label="Back"
          variant="tab"
          @click="closeDialog"
        />
        <AnButton
          :label="props.mode === 'REDEEM' ? 'Update' : 'Add'"
          variant="primary"
          :disabled="!isFieldsEnabled"
          @click="addMutualFund"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

/* Mode Toggle Styling - Left Top Corner */
.mode-toggle-container-left {
  position: absolute;
  left: 20px;
  top: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.mode-toggle-btn-small {
  min-width: 60px !important;
  font-size: 12px !important;
  padding: 6px 12px !important;
  height: 32px;
}

.step-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.step-section.disabled-section {
  opacity: 0.6;
  background: #f1f5f9;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.step-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #cbd5e1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background: var(--an-primary);
}

.step-number {
  font-size: 12px;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.stock-grid {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 100%;
}

.stock-grid > div:nth-child(2) {
  grid-column: 2;
}

.transaction-grid {
  grid-template-columns: repeat(3, 1fr);
  max-width: 100%;
}

.input-field {
  padding: 16px;
  border: 2px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 14px;
  width: 100%;
  max-width: 280px;
}

.autocomplete-input {
  max-width: 350px;
}

.current-price-field {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

:deep(.p-dialog) {
  width: 30vw !important;
  max-width: 550px !important;
  min-width: 450px !important;
}

:deep(.p-dialog .p-dialog-content) {
  padding: 0 !important;
}

.custom-autocomplete {
  position: relative;
  width: 100%;
}

.autocomplete-input {
  width: 100%;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1050;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  max-width: 350px;
}

.dropdown-item {
  padding: 6px 12px;
  font-size: 16px;
  color: #374151;
  cursor: pointer;
  background-color: white;
  transition: all 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #3b82f6;
  color: white;
}

.error-msg {
  color: #c92a2a;
  font-size: 1rem;
  margin: 0 32px 8px 32px;
}

.disabled-field {
  background-color: #f3f4f6 !important;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.border-red-500 {
  border-color: #ef4444 !important;
}

.text-muted {
  color: #6b7280;
  font-size: 12px;
}

.text-sm {
  font-size: 14px;
}

@media (max-width: 700px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }
  .ph-30.pb-30 {
    padding-left: 8px !important;
    padding-right: 8px !important;
    padding-bottom: 8px !important;
  }
  .step-section {
    padding: 12px 6px 12px 6px;
    margin-bottom: 12px;
  }
  .d-grid.stock-grid, .d-grid.transaction-grid, .add-row {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
  }
  .add-col, .d-flex.flex-direction-column {
    min-width: 0;
    width: 100%;
  }
  .input-field, .autocomplete-input {
    font-size: 1rem;
    padding: 12px;
    max-width: 100%;
  }
  .dialog-header span, .add-title {
    font-size: 1.2rem !important;
  }
  .add-label, .add-section-title, .step-title, .font-weight-600 {
    font-size: 1rem !important;
  }
  .add-footer, .d-flex.justify-center.gap-20.w-100.p-10 {
    flex-direction: column;
    gap: 12px;
    padding: 0 0 12px 0;
  }
}
</style>

<!-- Check AddMutualFundDialog.vue for the same logic issue -->
<!-- Need to add proper null/undefined checks for mutualFundsOwned -->
<!-- Similar to the fix applied to AddStockDialog.vue -->
